import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { ChevronUp, PauseCircle, Wifi, WifiOff } from 'lucide-react'
import { useEffect, useMemo } from 'react'

import { jobsQuery, jobStatsQuery, statsToQueueStats } from '@/api/jobs'
import { QueuePanel } from '@/components/QueuePanel'
import { cn } from '@/components/ui/utils'
import { formatClock, formatUsd } from '@/lib/format'
import { selectActiveJobs, selectBusy, useQueueStore } from '@/stores/queueStore'
import { useUiStore } from '@/stores/uiStore'

/**
 * Bottom strip: one line of queue truth — running/queued counts, the current
 * phase of the newest running job, a pause notice, spend today — and a
 * chevron that expands the jobs panel. It also *hydrates* `queueStore` from
 * `GET /api/jobs` and `GET /api/jobs/stats`; after that SSE keeps it fresh.
 */
export function QueueStrip() {
  const open = useUiStore((s) => s.queuePanelOpen)
  const setOpen = useUiStore((s) => s.setQueuePanelOpen)
  const stats = useQueueStore((s) => s.stats)
  const busy = useQueueStore(selectBusy)
  const connected = useQueueStore((s) => s.connected)
  // Select the stable array and derive; a selector that returns a fresh
  // `filter()` result every render makes useSyncExternalStore loop (React #185).
  const allJobs = useQueueStore((s) => s.jobs)
  const active = useMemo(() => selectActiveJobs({ jobs: allJobs }), [allJobs])
  const hydrate = useQueueStore((s) => s.hydrate)
  const setStats = useQueueStore((s) => s.setStats)

  const jobs = useQuery({ ...jobsQuery({ limit: 50 }), retry: 1, refetchInterval: connected ? false : 15_000 })
  const jobStats = useQuery({ ...jobStatsQuery, refetchInterval: connected ? 60_000 : 15_000 })

  useEffect(() => {
    if (jobs.data) hydrate(jobs.data.items)
  }, [jobs.data, hydrate])
  useEffect(() => {
    if (jobStats.data) setStats(statsToQueueStats(jobStats.data, useQueueStore.getState().stats))
  }, [jobStats.data, setStats])

  const newest = active.find((j) => j.status === 'running') ?? active[0]
  const queued = stats?.queued ?? active.filter((j) => j.status === 'queued').length
  const running = stats?.running ?? active.filter((j) => j.status === 'running').length
  const paused = stats?.paused_until ? { until: stats.paused_until, why: stats.paused_for } : null

  let summary: string
  if (jobs.isError && jobStats.isError) summary = 'Queue unavailable'
  else if (!busy) summary = 'Idle'
  else summary = [running ? `${running} running` : null, queued ? `${queued} queued` : null].filter(Boolean).join(' · ')

  return (
    <>
      {open ? (
        <div className="fixed inset-x-0 bottom-10 z-40 border-t border-line bg-paper shadow-pop" style={{ height: useUiStore.getState().panels.queuePanel }}>
          <QueuePanel compact />
        </div>
      ) : null}
      <div className="fixed inset-x-0 bottom-0 z-40 flex h-10 items-center gap-3 border-t border-line bg-paper-2/95 px-3 text-xs text-ink-2 backdrop-blur">
        <button
          type="button"
          onClick={() => setOpen(!open)}
          aria-expanded={open}
          aria-controls="queue-panel"
          className="inline-flex h-8 items-center gap-2 rounded-lg px-2 hover:bg-paper-3 hover:text-ink focus-visible:focus-ring"
        >
          <span className={cn('size-2 rounded-full', busy ? 'animate-pulse-soft bg-accent' : 'bg-ok')} aria-hidden />
          <span className="font-medium text-ink">{summary}</span>
          <ChevronUp className={cn('size-4 transition-transform', open && 'rotate-180')} aria-hidden />
        </button>

        {newest ? (
          <span className="hidden min-w-0 flex-1 truncate sm:inline" title={newest.progress.msg ?? undefined}>
            {newest.progress.msg ?? newest.progress.phase ?? newest.type}
            {newest.progress.pct != null ? ` · ${Math.round(newest.progress.pct * 100)} %` : ''}
          </span>
        ) : (
          <span className="hidden flex-1 sm:inline" />
        )}

        {paused ? (
          <span className="inline-flex items-center gap-1 text-warn" title={paused.why ?? 'paused'}>
            <PauseCircle className="size-3.5" aria-hidden />
            {paused.why === 'usage_window' ? 'Usage limit' : 'Paused'} — resumes {formatClock(paused.until)}
          </span>
        ) : null}

        {stats ? (
          <span className="hidden md:inline" title="List-price estimate, not a bill">
            Today {formatUsd(stats.spent_usd)}
          </span>
        ) : null}

        <Link to="/jobs" search={{}} className="hidden rounded-md px-1 hover:text-ink focus-visible:focus-ring md:inline">
          All jobs
        </Link>

        <span className="inline-flex items-center" title={connected ? 'Live updates connected' : 'Live updates disconnected — polling'}>
          {connected ? <Wifi className="size-3.5 text-ok" aria-hidden /> : <WifiOff className="size-3.5 text-ink-3" aria-hidden />}
          <span className="sr-only">{connected ? 'Live updates connected' : 'Live updates disconnected'}</span>
        </span>
      </div>
    </>
  )
}
